import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { toast } from "helpers/toast";
import { getModule } from "store/userSlice";
import { getAllCustomNames } from "store/fieldsSlice";
import { fetchBalanceScorecardList, getBalanceScorecardList } from "store/balanceScorecardSlice";
import { fetchBudgetSettings, getBudgetSettings } from "store/budgetSettingsSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import EmployeeSelector from "./EmployeeSelector";
import ListPagination from "components/ui/ListPagination";
import ActionChangePreview from "./ActionChangePreview";
import { CHANGE_FIRST_FIELDS, changeFirstFieldsFor, valueKeysFor, hasCheckedField, valuesComplete, buildChangeFirstOverwritePayload, buildChangeFirstInstancePayload } from "./changeFirstScorecardHelpers";
import { applySelectionChange, buildWhoList, pairInstanceValues } from "./actionChangePreviewHelpers";
import { isDbModule } from "helpers/module";
import { Button, Input, InputAffix, Modal, Select } from "components/ui";
import useEmployeePool from "./useEmployeePool";
import { employeeManagementPath } from "helpers/employeeDeeplink";
import sanitize from "helpers/sanitize";

const SKIP_REASONS = ['no_scorecard', 'completed', 'snapshot'];

// Texts that name the card: the DB module says "Beurteilung" where LOB says "Leistungskarte".
const CARD_TEXTS = {
  description: {
    lob: 'Geändert wird nur die erste Leistungskarte. Ein Wechsel der Vorlage verwirft alle Bewertungsdaten der Karte.',
    db: 'Geändert wird nur die erste Beurteilung. Ein Wechsel der Vorlage verwirft alle Beurteilungsdaten.',
  },
  'skipped.no_scorecard': { lob: 'Übersprungen, keine Leistungskarte:', db: 'Übersprungen, keine Beurteilung:' },
  'skipped.completed': { lob: 'Übersprungen, erste Leistungskarte abgeschlossen:', db: 'Übersprungen, erste Beurteilung abgeschlossen:' },
  'skipped.snapshot': {
    lob: 'Übersprungen, erste Leistungskarte bereits in Berechnung oder Auszahlung eingeflossen:',
    db: 'Übersprungen, erste Beurteilung bereits in Berechnung oder Auszahlung eingeflossen:',
  },
};

// Embedded in FillFieldsAction as the "Erste Leistungskarte ändern" tab. Changes template, budget and/or
// group of each selected employee's first scorecard; the backend preview feeds a mandatory warning.
export default function ChangeFirstScorecardAction({ filters, page, setPage, setFilterCount, tabs, activeTab, onTabChange, onBack, sharedSelection, sharedPool }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const currentModule = useSelector(getModule);
  const isDbModuleActive = useMemo(() => isDbModule(currentModule), [currentModule]);

  const fieldCustomNames = useSelector(getAllCustomNames);
  const balancedScorecardList = useSelector(getBalanceScorecardList);
  const budgets = useSelector(getBudgetSettings);
  const groups = useSelector(getGroups);

  const { actionMode, setActionMode, selected, setSelected, selectedEmployees, setSelectedEmployees } = sharedSelection;

  const [checked, setChecked] = useState({}); // overwrite: { template|budget|group: true }
  const [values, setValues] = useState({}); // overwrite: { [valueKey]: hash_id }
  const [checkedEmployees, setCheckedEmployees] = useState({}); // per-instance: hash_id -> display data
  const [instanceChecked, setInstanceChecked] = useState([]); // per-instance: checked hash_ids
  const [instanceValues, setInstanceValues] = useState({}); // per-instance: { [hash_id]: { [valueKey]: hash_id } }
  const [instanceFieldChecked, setInstanceFieldChecked] = useState({}); // per-instance: { [hash_id]: { template|budget|group: true } }
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Per-instance keeps its own pool; overwrite reuses the parent's selector pool.
  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [poolSearch, setPoolSearch] = useState('');

  useEffect(() => {
    if (!balancedScorecardList) dispatch(fetchBalanceScorecardList());
    if (!budgets) dispatch(fetchBudgetSettings());
    if (!groups) dispatch(fetchGroups());
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { employees: localEmployees, pagination, loading: localLoadingPool, loaded: localPoolLoaded, error: poolError } = useEmployeePool({
    filters,
    page: poolPage,
    perPage: poolPerPage,
    search: poolSearch,
    enabled: actionMode !== 'overwrite',
    onTotal: setFilterCount,
  });

  useEffect(() => {
    if (poolError) setError(poolError);
  }, [poolError]);

  const handlePoolPerPageChange = (n) => {
    setPoolPerPage(n);
    setPoolPage(1);
  };

  const { employees, poolLoaded, poolPaginationProps } = actionMode === 'overwrite' ? sharedPool : {
    employees: localEmployees, poolLoaded: localPoolLoaded,
  };

  // ---- option lists ----

  const templateOptions = useMemo(() => (balancedScorecardList || [])
    .filter(bs => isDbModuleActive ? bs?.form_definition?.type === 'custom_balanced_scorecard_setting' : bs?.form_definition?.type !== 'custom_balanced_scorecard_setting'), [balancedScorecardList, isDbModuleActive]);

  const templateGroupOptions = useMemo(() => {
    const groupsMap = new Map();
    (balancedScorecardList || []).forEach((bs) => {
      const group = bs?.balanced_scorecard_setting_group;
      if (group?.hash_id && !groupsMap.has(group.hash_id)) groupsMap.set(group.hash_id, { name: group.name, hash_id: group.hash_id });
    });
    return Array.from(groupsMap.values());
  }, [balancedScorecardList]);

  const templateOptionsForGroup = (groupHashId) => (balancedScorecardList || [])
    .filter(bs => groupHashId && bs?.balanced_scorecard_setting_group?.hash_id === groupHashId)
    .map(bs => {
      const label = bs?.balanced_scorecard_setting_group?.name
        ? bs.slug.replace(new RegExp(`^${bs.balanced_scorecard_setting_group.name}\\s*-\\s*`, 'i'), '')
        : bs.slug;
      return { name: translate('state_employee.rating_mode.template_' + label?.toLowerCase(), label), hash_id: bs.hash_id };
    });

  const optionsForKey = (key, vals) => {
    if (key === 'balanced_scorecard_template') return isDbModuleActive ? templateOptionsForGroup(vals?.balanced_scorecard_template_group) : templateOptions;
    if (key === 'balanced_scorecard_template_group') return templateGroupOptions;
    if (key === 'budget') return budgets || [];
    if (key === 'group') return groups || [];
    return [];
  };

  const fieldLabels = {
    template: isDbModuleActive
      ? editableTranslate('action.detail.change_first.field.template_db', 'Beurteilungsvorlage')
      : (fieldCustomNames?.['field.balanced_scorecard_template'] || translate('field.balanced_scorecard_template')),
    budget: fieldCustomNames?.['field.budget'] || translate('field.budget'),
    group: fieldCustomNames?.['field.group'] || translate('field.group'),
  };
  const valueLabels = {
    balanced_scorecard_template_group: fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode'),
    balanced_scorecard_template: isDbModuleActive ? translate('state_employee.rating_mode.template') : fieldLabels.template,
    budget: fieldLabels.budget,
    group: fieldLabels.group,
  };

  const cardText = (key) => isDbModuleActive
    ? editableTranslate(`action.detail.change_first.${key}_db`, CARD_TEXTS[key].db)
    : editableTranslate(`action.detail.change_first.${key}`, CARD_TEXTS[key].lob);

  const fields = changeFirstFieldsFor(isDbModuleActive);
  const checkedValueKeys = (fieldChecked) => fields.filter(field => fieldChecked?.[field]).flatMap(field => valueKeysFor(field, isDbModuleActive));

  // The DB template list depends on the template group, so a new group clears the template.
  const withValue = (prev, key, value) => ({
    ...prev,
    [key]: value,
    ...(key === 'balanced_scorecard_template_group' ? { balanced_scorecard_template: '' } : {}),
  });

  const setInstanceValue = (hashId, key, value) => setInstanceValues(prev => ({ ...prev, [hashId]: withValue(prev[hashId] || {}, key, value) }));
  const setInstanceFieldCheck = (hashId, field, isChecked) => setInstanceFieldChecked(prev => ({ ...prev, [hashId]: { ...prev[hashId], [field]: isChecked } }));

  const toggleInstanceRow = (hashId, isChecked) => {
    setInstanceChecked(prev => isChecked ? (prev.includes(hashId) ? prev : [...prev, hashId]) : prev.filter(id => id !== hashId));
    const employee = (employees || []).find(e => e.hash_id === hashId) || { hash_id: hashId };
    setCheckedEmployees(prev => applySelectionChange(prev, [employee], isChecked));
  };

  const changeActionMode = (mode) => {
    if (mode === actionMode) return;
    setActionMode(mode);
    setSearchInput('');
    setPoolSearch('');
    setPoolPage(1);
  };

  // ---- step transitions / apply ----

  const buildPayload = () => (
    actionMode === 'overwrite'
      ? buildChangeFirstOverwritePayload({ filters, selected, checked, values })
      : buildChangeFirstInstancePayload({ instanceChecked, instanceValues, checked: instanceFieldChecked })
  );

  const goToPreview = () => {
    setError(null);

    const chosen = actionMode === 'overwrite' ? selected : instanceChecked;
    if (chosen.length < 1) {
      setError(editableTranslateString('batch_update.checked_employees_required'));
      return;
    }
    if (actionMode === 'overwrite' ? !hasCheckedField(checked) : !instanceChecked.every(hash => hasCheckedField(instanceFieldChecked[hash]))) {
      setError(editableTranslateString('action.detail.change_first.field_required', 'Bitte wählen Sie mindestens ein Feld aus.'));
      return;
    }
    const complete = actionMode === 'overwrite'
      ? valuesComplete(checked, values, isDbModuleActive)
      : instanceChecked.every(hash => valuesComplete(instanceFieldChecked[hash], instanceValues[hash], isDbModuleActive));
    if (!complete) {
      setError(editableTranslateString('action.detail.change_first.value_required', 'Bitte füllen Sie alle ausgewählten Felder aus.'));
      return;
    }

    setPreview(null);
    setPage(2);
  };

  // Always re-run the dry run on the way to step 3, so the warning never shows stale counts.
  const goToApply = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const { data, status } = await api.post('/employee-balanced-scorecards/change-first/preview', buildPayload());
      if (status === 200) {
        setPreview(data?.data || null);
        setPage(3);
      }
    } catch (err) {
      setError(errorMessage(err, 'pre-check change first scorecard'));
      Sentry.captureException(err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { status } = await api.post('/employee-balanced-scorecards/change-first', buildPayload());
      if (status !== 200) throw new Error('unexpected status');

      toast.success(translate('form.success.saved'));
      navigate('/settings/actions');
    } catch (err) {
      setConfirming(false);
      setError(errorMessage(err, 'apply change first scorecard action'));
      Sentry.captureException(err);
      setSubmitting(false);
    }
  };

  // ---- render helpers ----

  // Like the update_data tab: the checkbox sits in front of the field label, picking a value ticks it.
  // The DB template has two selects (template group, template) that share the template checkbox.
  const renderFieldCells = (fieldChecked, onCheck, vals, onValue, idSuffix = '') => fields.flatMap(field => valueKeysFor(field, isDbModuleActive).map(key => {
    const opts = optionsForKey(key, vals).map(item => ({ value: item.hash_id, label: item.name }));
    const cbId = `change-first-checkbox-${key}${idSuffix}`;
    return (
      <div key={key} className="col-xl-4 col-lg-6 form-group">
        <div className="form-check mb-1">
          <input type="checkbox" id={cbId} data-testid={cbId} className="form-check-input" checked={!!fieldChecked?.[field]} onChange={e => onCheck(field, e.target.checked)} />
          <label className="form-check-label ml-1" htmlFor={cbId}>{valueLabels[key]}</label>
        </div>
        <div style={{ display: 'contents' }} data-testid={`change-first-${key}-select`}>
          <Select
            inputId={`change-first-${key}${idSuffix}`}
            options={opts}
            value={opts.find(o => o.value === vals?.[key]) || null}
            onChange={option => { onValue(key, option ? option.value : ''); onCheck(field, true); }}
            isClearable
            placeholder={translate('form.select_placeholder')}
          />
        </div>
      </div>
    );
  }));

  const renderInstanceEditor = () => (
    <div>
      <div className="mb-3">{editableTranslate('action.detail.fill_per_employee_text', 'Wählen Sie Beschäftigte aus und füllen Sie die Felder aus.')}</div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
          <Input data-testid="search-input" type="text" placeholder={translate('global.search', 'Suche')} maxLength={255} value={searchInput}
            onChange={e => setSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
        </InputAffix>
        <div className="text-muted text-nowrap ml-2">{instanceChecked.length} / {pagination.total}</div>
      </div>

      {localLoadingPool && <div className="text-center p-2" data-testid="loading">
        <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
      </div>}

      {(employees || []).length === 0
        ? (!localLoadingPool && <div className="text-muted p-2" data-testid="empty-state">{editableTranslate('global.no_data', 'Keine Daten')}</div>)
        : (employees || []).map(employee => {
          const isChecked = instanceChecked.includes(employee.hash_id);
          return (
            <div data-testid={`employee-instance-${employee.hash_id}`} key={employee.hash_id} className="border-bottom py-2">
              <div className="form-check">
                <label className="form-check-label">
                  <input className="form-check-input" type="checkbox" checked={isChecked} onChange={e => toggleInstanceRow(employee.hash_id, e.target.checked)} data-testid={`employee-instance-check-${employee.hash_id}`} />
                  <span className="ml-1">
                    {`${employee.first_name || ''} ${employee.last_name || ''}`.trim()}
                    {employee.employee_number ? <span className="text-muted ml-1">({employee.employee_number})</span> : null}
                  </span>
                </label>
              </div>
              {isChecked && <div className="p-3 bg-light rounded mb-2">
                <div className="row">
                  {renderFieldCells(
                    instanceFieldChecked[employee.hash_id],
                    (field, value) => setInstanceFieldCheck(employee.hash_id, field, value),
                    instanceValues[employee.hash_id],
                    (k, v) => setInstanceValue(employee.hash_id, k, v),
                    `-${employee.hash_id}`,
                  )}
                </div>
              </div>}
            </div>
          );
        })}

      <ListPagination page={pagination.currentPage} perPage={pagination.perPage} totalPages={pagination.totalPages} totalRows={pagination.total} setPage={setPoolPage} setPerPage={handlePoolPerPageChange} />
    </div>
  );

  const chosenValues = (fieldChecked, vals) => checkedValueKeys(fieldChecked)
    .filter(key => key !== 'balanced_scorecard_template_group')
    .map(key => ({ label: valueLabels[key], value: optionsForKey(key, vals).find(o => o.hash_id === vals?.[key])?.name || '—' }));

  const employeeName = (e) => e.full_name ? `${e.full_name} (${e.employee_number})` : e.employee_number;

  // Same as the create tab: one employee per line, opening employee management in a new tab.
  const employeeLinks = (list) => list.map(e => (
    <div key={e.employee_hash_id || e.employee_number}>
      - <a href={employeeManagementPath({ staffId: e.employee_number })} target="_blank" rel="noopener noreferrer">{e.full_name || e.employee_number}</a>
    </div>
  ));

  const renderChange = (row) => CHANGE_FIRST_FIELDS
    .filter(field => row[field])
    .map((field, i) => <span key={field}>{i > 0 && ', '}{fieldLabels[field]}: {row[field].from || '—'} → {row[field].to}</span>);

  const totals = preview?.totals || {};
  const warningLines = [
    totals.template_count > 0 && editableTranslateString('action.detail.change_first.warning.template', '$X Beschäftigte erhalten eine neue Vorlage. $Y davon verlieren ihre Bewertungsdaten.')
      .replace('$X', totals.template_count).replace('$Y', totals.evaluation_loss_count),
    totals.complaint_count > 0 && editableTranslateString('action.detail.change_first.warning.complaints', '$X Beschwerden werden gelöscht.').replace('$X', totals.complaint_count),
    totals.budget_count > 0 && editableTranslateString('action.detail.change_first.warning.budget', '$X Beschäftigte erhalten ein neues Budget.').replace('$X', totals.budget_count),
    (preview?.reset_budgets || []).length > 0 && editableTranslateString('action.detail.change_first.warning.reset_budgets', 'Die Berechnung wird für folgende Budgets zurückgesetzt: $X').replace('$X', preview.reset_budgets.map(b => b.name).join(', ')),
    totals.group_count > 0 && editableTranslateString('action.detail.change_first.warning.group', '$X Beschäftigte erhalten eine neue Gruppe.').replace('$X', totals.group_count),
  ].filter(Boolean);

  return (
    <div className="mt-3" data-testid="change-first-scorecard">
      {error && <div className="mb-3">
        <ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} type="danger" />
      </div>}

      {page === 1 && <>
        {actionMode === 'overwrite' && <div className="mb-5">
          <div className="font-weight-bold">{editableTranslate('action.detail.employees', 'Beschäftigte')}</div>
          <hr className="my-2" />
          <div className="mb-3">{editableTranslate('action.detail.select_employees', 'Wählen Sie die Beschäftigten aus, auf die die Änderungen angewendet werden sollen.')}</div>
          {!poolLoaded
            ? <div className="text-center p-4" data-testid="loading"><div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div></div>
            : <EmployeeSelector employees={employees || []} selected={selected} setSelected={setSelected} onSelectionChange={(emps, isChecked) => setSelectedEmployees(prev => applySelectionChange(prev, emps, isChecked))} {...poolPaginationProps} />}
        </div>}

        <div className="mb-3">
          <div className="action-tab-mode-div">
            <div className="flex-1 border-bottom">
              <div className="employee-management-tabs">
                {tabs.map((tab) => (
                  <div data-testid={`action-tab-${tab.key}`} key={tab.key} className={`cursor-pointer ${activeTab === tab.key ? 'active' : ''}`} onClick={() => onTabChange(tab.key)}>
                    <i className={`bi ${tab.icon} mr-1`}></i>
                    <span>{tab.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="btn-group btn-group-switch-mode normal-border padding-normal" role="group">
              <button type="button" className={`btn ${actionMode === 'overwrite' ? ' active' : ''}`} onClick={() => changeActionMode('overwrite')} data-testid="mode-overwrite-button">
                {editableTranslate('action.detail.mode.overwrite', 'Überschreiben')}
              </button>
              <button type="button" className={`btn ${actionMode === 'per_instance' ? ' active' : ''}`} onClick={() => changeActionMode('per_instance')} data-testid="mode-per-instance-button">
                {editableTranslate('action.detail.mode.per_instance', 'Pro Beschäftigte ausfüllen')}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-3">{cardText('description')}</div>

        {actionMode === 'overwrite'
          ? <div className="row" data-testid="change-first-fields">
            {renderFieldCells(checked, (field, value) => setChecked(prev => ({ ...prev, [field]: value })), values, (k, v) => setValues(prev => withValue(prev, k, v)))}
          </div>
          : renderInstanceEditor()}

        <div className="settings-sticky-footer mt-3">
          {onBack
            ? <Button variant="secondary" onClick={onBack} data-testid="back-button" className="mr-2">{editableTranslate('button.back', 'Zurück')}</Button>
            : <Button data-testid="back-button" as={Link} variant="secondary" className="mr-2" to="/settings/actions">{editableTranslate('button.back', 'Zurück')}</Button>}
          <Button variant="primary" onClick={goToPreview} disabled={!poolLoaded || (actionMode === 'overwrite' ? selected.length === 0 : instanceChecked.length === 0)} data-testid="next-button">
            {editableTranslate('button.next', 'Weiter')}
          </Button>
        </div>
      </>}

      {page === 2 && <>
        {actionMode === 'overwrite'
          ? <ActionChangePreview whatItems={chosenValues(checked, values)} whoList={buildWhoList(selected, selectedEmployees)} actionType={'change_first_scorecard'} />
          : <ActionChangePreview perInstance whoList={pairInstanceValues(instanceChecked, checkedEmployees, hash => chosenValues(instanceFieldChecked[hash], instanceValues[hash]))} actionType={'change_first_scorecard'} />}

        <div className="settings-sticky-footer d-flex mt-3">
          <Button variant="secondary" onClick={() => setPage(1)} disabled={loadingPreview} data-testid="back-button" className="mr-2">{editableTranslate('button.back', 'Zurück')}</Button>
          <Button variant="primary" onClick={goToApply} loading={loadingPreview} data-testid="proceed-button">{editableTranslate('button.proceed', 'Fortfahren')}</Button>
        </div>
      </>}

      {page === 3 && preview && <>
        <div className="font-weight-bold">{editableTranslate('action.detail.summary', 'Zusammenfassung')}</div>
        <hr className="mt-2" />

        <div className="mb-3" data-testid="change-first-changed">
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: editableTranslateString('action.detail.change_first.summary', '$X Leistungskarten von Beschäftigten werden geändert.').replace('$X', preview.changed.length) }} />
          {preview.changed.length > 0 && <ul className="pl-4 mb-0 action-change-preview-list">
            {preview.changed.map(row => (
              <li key={row.employee_hash_id} className="mb-1" data-testid={`change-first-changed-${row.employee_hash_id}`}>
                {employeeName(row)}
                {row.loses_evaluation_data && <span className="text-danger ml-1">({editableTranslate('action.detail.change_first.loses_data', 'Bewertungsdaten gehen verloren')})</span>}
              </li>
            ))}
          </ul>}
        </div>

        {(SKIP_REASONS.some(reason => preview.skipped[reason].length > 0) || preview.unchanged.length > 0) && <div className="alert alert-warning" data-testid="change-first-skipped">
          {SKIP_REASONS.filter(reason => preview.skipped[reason].length > 0).map(reason => (
            <div key={reason} className="mb-2" data-testid={`change-first-skipped-${reason}`}>
              <div>{cardText(`skipped.${reason}`)}</div>
              {employeeLinks(preview.skipped[reason])}
            </div>
          ))}
          {preview.unchanged.length > 0 && <div data-testid="change-first-unchanged">
            <div>{editableTranslate('action.detail.change_first.unchanged', 'Keine Änderung, Werte bereits gesetzt:')}</div>
            {employeeLinks(preview.unchanged)}
          </div>}
        </div>}

        <div className="settings-sticky-footer d-flex mt-3">
          <Button variant="secondary" onClick={() => setPage(2)} disabled={submitting} data-testid="back-button" className="mr-2">{editableTranslate('button.back', 'Zurück')}</Button>
          <Button variant="primary" onClick={() => setConfirming(true)} disabled={preview.changed.length === 0} data-testid="apply-button">{editableTranslate('button.apply', 'Anwenden')}</Button>
        </div>

        <Modal
          open={confirming}
          title={translate('action.detail.change_first.warning.title', 'Änderung bestätigen')}
          onClose={() => setConfirming(false)}
          onConfirm={handleApply}
          okLabel={translate('action.detail.change_first.warning.confirm', 'Endgültig anwenden')}
          cancelLabel={translate('button.cancel', 'Abbrechen')}
          danger
          loading={submitting}
        >
          <div data-testid="change-first-warning">
            <ul className="pl-4">
              {warningLines.map((line, i) => <li key={i} dangerouslySetInnerHTML={{ __html: sanitize(line) }} />)}
            </ul>
            <div className="font-weight-bold text-danger">{editableTranslate('action.detail.change_first.warning.irreversible', 'Diese Änderung kann nicht rückgängig gemacht werden.')}</div>
          </div>
        </Modal>
      </>}
    </div>
  );
}
